import {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import { motion, AnimatePresence } from "framer-motion";
import AdminLayout from "../../layouts/AdminLayout";
import ManagerLayout from "../../layouts/ManagerLayout";
import EmployeeLayout from "../../layouts/EmployeeLayout";
import { getLeaves, raiseEditTicket, getEditTickets, editLeave, cancelEditTicket } from "../../api/attendanceService";
import apiClient from "../../api/apiClient";

const LEAVE_TYPES = [
  { value: "CASUAL", label: "Casual Leave", singleDay: false, isCL: true },
  { value: "HALF CASUAL", label: "Half Day Casual", singleDay: true, isCL: true },
  { value: "SICK", label: "Sick Leave", singleDay: false, isCL: false },
  { value: "MEDICAL", label: "Medical Leave", singleDay: false, isCL: false },
  { value: "HALF DAY", label: "Half Day", singleDay: true, isCL: false },
  { value: "OTHER", label: "Other", singleDay: false, isCL: false },
  { value: "COMP_OFF", label: "Comp Off", singleDay: true, isCL: false },
];
const LEAVE_STATUSES = ["Pending", "Approved", "Rejected"];
const SINGLE_DAY_TYPES = new Set(["HALF CASUAL", "HALF DAY", "COMP_OFF"]);

// ── sentinel that matches models.py / management command ─────────────────────
const AUTO_UNPAID_REASON = "Auto: no attendance recorded for remaining half-day.";

// ── half-day session labels ───────────────────────────────────────────────────
const SESSION_LABEL = {
  HALF_DAY_FIRST: "Morning",
  HALF_DAY_SECOND: "Afternoon",
};

function sanitizeText(str) {
  if (!str) return "";
  return str
    .replace(/<[^>]*>/g, "") // strip HTML tags
    .replace(/[;'"\\]/g, "") // strip database escape codes
    .trim();
}

function SessionBadge({ session }) {
  if (!session) return null;
  const label = SESSION_LABEL[session] || session;
  const cls =
    session === "HALF_DAY_FIRST"
      ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
      : "bg-sky-500/20 text-sky-300 border-sky-500/30";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold border leading-none ml-1 ${cls}`}>
      {label}
    </span>
  );
}

function AutoUnpaidBadge() {
  return (
    <span
      title="Auto-created: employee did not mark attendance for the remaining half"
      className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold border leading-none ml-1
                 bg-orange-500/20 text-orange-300 border-orange-500/30"
    >
      AUTO
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Approved: "bg-emerald-500/30 text-emerald-200 border-emerald-400/30",
    Rejected: "bg-red-500/30 text-red-200 border-red-400/30",
    Pending: "bg-yellow-500/30 text-yellow-200 border-yellow-400/30",
  };
  return (
    <span className={`px-2 py-0.5 sm:py-1 rounded-lg text-xs border font-semibold ${styles[status] || styles.Pending}`}>
      {status}
    </span>
  );
}

function validateEditForm(form, leaveTypes = LEAVE_TYPES) {
  const { leave_type, leave_date_from, leave_date_to } = form;
  if (!leave_type) return "Leave type is required.";
  if (!leave_date_from) return "Leave From date is required.";
  if (!leave_date_to) return "Leave To date is required.";
  if (leave_date_to < leave_date_from) return "Leave To date cannot be before Leave From.";
  if (SINGLE_DAY_TYPES.has(leave_type) && leave_date_from !== leave_date_to) {
    const matched = leaveTypes.find(t => t.value === leave_type);
    return `"${matched?.label ?? matched?.name ?? leave_type}" must be a single day.`;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// LeaveReport
// ─────────────────────────────────────────────────────────────────────────────

function LeaveReport({ isPopup = false, onClose = null }) {
  const role = useAuthStore((state) => state.role);
  const loggedInEmployeeId = useAuthStore((state) => state.employeeId);
  const Layout = role === "ADMIN" ? AdminLayout : role === "MANAGER" ? ManagerLayout : EmployeeLayout;
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const targetEmployee = employeeId || loggedInEmployeeId;
  const isOwnReport = targetEmployee === loggedInEmployeeId;
  const currentDate = new Date();
  const DEFAULT_MONTH = String(currentDate.getMonth() + 1);
  const DEFAULT_YEAR = String(currentDate.getFullYear());

  const [leaveSummary, setLeaveSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [showEditColumn, setShowEditColumn] = useState(isPopup);
  const [showTicketReasonColumn] = useState(isPopup);
  const [showSessionColumn] = useState(isPopup);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [pendingTicketCount, setPendingTicketCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    if (loggedInEmployeeId) {
      apiClient.get(`/accounts/api/users/${loggedInEmployeeId}/`)
        .then((res) => setIsSuperAdmin(res.data.user?.is_superuser === true))
        .catch(() => setIsSuperAdmin(false));
    }
  }, [loggedInEmployeeId]);

  const loadPendingTicketCount = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await getEditTickets({ status: "PENDING", employee_id: targetEmployee });
      const tickets = res.data.tickets || [];
      const count = tickets.filter((t) => t.request_type === "LEAVE").length;
      setPendingTicketCount(count);
    } catch { setPendingTicketCount(0); }
  }, [isSuperAdmin, targetEmployee]);

  useEffect(() => { loadPendingTicketCount(); }, [loadPendingTicketCount]);
  const [ticketMap, setTicketMap] = useState({});
  const [ticketModal, setTicketModal] = useState(null);
  const [ticketReason, setTicketReason] = useState("");
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [leaveTypes, setLeaveTypes] = useState(LEAVE_TYPES);

  // Direct document preview and direct download (no popups)
  const handleDirectPreview = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDirectDownload = async (url, fileName) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "medical_document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "medical_document";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ── data loading — leaves + tickets fetched in parallel ──────────────────

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const leaveParams = {
        employee_id: role !== "EMPLOYEE" ? targetEmployee : undefined,
        month: selectedMonth === "0" ? undefined : selectedMonth,
        year: selectedYear === "0" ? undefined : selectedYear,
      };
      // Fetch leaves and tickets simultaneously
      const [res, ticketRes] = await Promise.all([
        getLeaves(leaveParams),
        getEditTickets({ employee_id: targetEmployee }),
      ]);

      const leaves = res.data.leaves || [];
      if (res.data && res.data.leave_types) {
        setLeaveTypes(res.data.leave_types);
      }

      const years = new Set();
      leaves.forEach((l) => years.add(new Date(l.leave_date_from).getFullYear()));
      setAvailableYears([...years].sort((a, b) => b - a));

      const grouped = {};
      leaves.forEach((leave) => {
        const from = new Date(leave.leave_date_from);
        const month = from.toLocaleString("default", { month: "long" });
        const year = from.getFullYear();
        const key = `${year}-${String(from.getMonth() + 1).padStart(2, "0")}`;

        if (!grouped[key])
          grouped[key] = { month, year, casualTaken: 0, compOffTaken: 0, extraTaken: 0, leaves: [] };

        const dateText =
          leave.leave_date_from === leave.leave_date_to
            ? new Date(leave.leave_date_from).toLocaleDateString("en-GB")
            : `${new Date(leave.leave_date_from).toLocaleDateString("en-GB")} – ${new Date(leave.leave_date_to).toLocaleDateString("en-GB")}`;

        const days = parseFloat(leave.total_days) || 1;
        const rawType = (leave.leave_type_raw ?? leave.leave_type ?? "").toUpperCase();
        const isAutoUnpaid = leave.reason === AUTO_UNPAID_REASON;

        grouped[key].leaves.push({
          id: leave.id,
          rawFrom: leave.leave_date_from,
          rawTo: leave.leave_date_to,
          date: dateText,
          type: leave.leave_type,
          rawType: leave.leave_type_raw ?? leave.leave_type,
          days,
          reason: leave.reason,
          status: leave.status,
          action_by: leave.action_by,
          halfDaySession: leave.half_day_session || null,
          isAutoUnpaid,
          medical_document: leave.medical_document,
          medical_document_name: leave.medical_document_name,
        });

        if (leave.status === "Approved") {
          if (rawType.includes("CASUAL")) {
            grouped[key].casualTaken += days;
          } else if (rawType === "COMP_OFF") {
            grouped[key].compOffTaken += days;
          } else {
            grouped[key].extraTaken += days;
          }
        }
      });

      setLeaveSummary(
        Object.entries(grouped)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([, v]) => v)
      );

      // Process tickets from the parallel fetch
      const tickets = (ticketRes.data.tickets || []).filter((t) => t.request_type === "LEAVE");
      const map = {};
      tickets.forEach((t) => {
        if (!map[t.reference_id])
          map[t.reference_id] = { id: t.id, status: t.status, reason: t.reason };
      });
      setTicketMap(map);

    } catch (err) {
      console.error("Leave fetch error:", err);
    } finally {
      setLoading(false);
      setCurrentPage(1);
      loadPendingTicketCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEmployee, selectedMonth, selectedYear, role]);

  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const loadTickets = useCallback(async () => {
    try {
      const res = await getEditTickets({ employee_id: targetEmployee });
      const tickets = (res.data.tickets || []).filter((t) => t.request_type === "LEAVE");
      const map = {};
      tickets.forEach((t) => {
        if (!map[t.reference_id])
          map[t.reference_id] = { id: t.id, status: t.status, reason: t.reason };
      });
      setTicketMap(map);
    } catch (err) {
      console.error("Ticket fetch error:", err);
    }
  }, [targetEmployee]);



  // ── derived values ────────────────────────────────────────────────────────

  const hasAnyCompOff = leaveSummary.some((r) => r.compOffTaken > 0);
  const hasAnySession = leaveSummary.some((r) =>
    r.leaves.some((l) => l.halfDaySession)
  );

  // ── actions ───────────────────────────────────────────────────────────────

  const handleRaiseTicket = async () => {
    const sanitizedReason = sanitizeText(ticketReason);
    if (!sanitizedReason) { setTicketError("Please provide a valid, safe reason."); return; }
    setTicketSubmitting(true);
    setTicketError("");
    try {
      await raiseEditTicket({
        request_type: "LEAVE",
        reference_id: ticketModal.leaveId,
        reason: sanitizedReason,
      });
      setTicketModal(null);
      setTicketReason("");
      loadTickets();
    } catch (err) {
      setTicketError(err.response?.data?.error || "Failed to raise ticket.");
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleCancelTicket = async (ticketId) => {
    setCancellingId(ticketId);
    try {
      await cancelEditTicket(ticketId);
      loadLeaves();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel ticket.");
    } finally {
      setCancellingId(null);
    }
  };

  const startEditing = (leave) => {
    setEditingId(leave.id);
    setEditError("");
    setEditForm({
      leave_date_from: leave.rawFrom,
      leave_date_to: leave.rawTo,
      leave_type: leave.rawType,
      reason: leave.reason || "",
      status: leave.status,
    });
  };
  const cancelEditing = () => { setEditingId(null); setEditError(""); };

  const handleTypeChange = (newType) => {
    setEditForm((prev) => ({
      ...prev,
      leave_type: newType,
      leave_date_to: SINGLE_DAY_TYPES.has(newType) ? prev.leave_date_from : prev.leave_date_to,
    }));
    setEditError("");
  };

  const handleFromChange = (newFrom) => {
    setEditForm((prev) => {
      const isSingle = SINGLE_DAY_TYPES.has(prev.leave_type);
      const newTo =
        isSingle ? newFrom
          : prev.leave_date_to < newFrom ? newFrom
            : prev.leave_date_to;
      return { ...prev, leave_date_from: newFrom, leave_date_to: newTo };
    });
    setEditError("");
  };

  const handleSaveEdit = async (leaveId) => {
    const validationError = validateEditForm(editForm, leaveTypes);
    if (validationError) { setEditError(validationError); return; }
    setEditSaving(true);
    setEditError("");
    try {
      await editLeave(leaveId, {
        leave_date_from: editForm.leave_date_from,
        leave_date_to: editForm.leave_date_to,
        leave_type: editForm.leave_type,
        reason: editForm.reason,
        status: editForm.status,
      });
      setEditingId(null);
      await Promise.all([loadLeaves(), loadTickets()]);
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to save changes.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── CSV download ──────────────────────────────────────────────────────────

  const downloadCSV = () => {
    if (leaveSummary.length === 0) return;
    const headers = ["Month-Year", "CL Taken"];
    if (hasAnyCompOff) headers.push("Comp Off Taken");
    headers.push("Extra Taken", "Date", "Type", "Session", "Reason", "Status", "Action By", "Ticket Reason");
    const rows = [headers];
    leaveSummary.forEach((record) => {
      record.leaves.forEach((leave, i) => {
        const ticket = ticketMap[leave.id];
        const sessionLabel =
          leave.halfDaySession ? (SESSION_LABEL[leave.halfDaySession] || leave.halfDaySession) : "";
        const row = [
          i === 0 ? `${record.month}-${record.year}` : "",
          i === 0 ? record.casualTaken : "",
        ];
        if (hasAnyCompOff) row.push(i === 0 ? record.compOffTaken : "");
        row.push(
          i === 0 ? record.extraTaken : "",
          leave.date,
          leave.type,
          sessionLabel,
          leave.reason || "",
          leave.status,
          leave.action_by || "—",
          ticket?.reason || "",
        );
        rows.push(row);
      });
    });
    const csv = "data:text/csv;charset=utf-8," + rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", `LeaveReport_${targetEmployee}_${selectedMonth}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── navigation ────────────────────────────────────────────────────────────

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (location.state?.from) navigate(location.state.from);
    else if (role === "ADMIN") navigate("/admin/dashboard", { state: { defaultTab: "leave" } });
    else if (role === "MANAGER") navigate("/manager/dashboard", { state: { defaultTab: "leave" } });
    else navigate("/employee/dashboard", { state: { defaultTab: "leave" } });
  };

  // ── inline sub-component ──────────────────────────────────────────────────

  const TicketCell = ({ leave }) => {
    const ticket = ticketMap[leave.id];
    if (editingId !== null && editingId === leave.id) return null;

    if (role === "ADMIN") {
      if (ticket?.status === "PENDING")
        return (
          <button
            onClick={() => startEditing(leave)}
            className="px-2 sm:px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-slate-900
                       text-xs font-bold transition flex items-center gap-1 mx-auto"
          >
            ✏️ Edit
            <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-900/30 text-[10px] font-semibold">Pending</span>
          </button>
        );
      if (ticket?.status === "APPROVED")
        return (
          <button
            onClick={() => startEditing(leave)}
            className="px-2 sm:px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white
                       text-xs font-bold transition flex items-center gap-1 mx-auto"
          >
            ✏️ Edit
            <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-semibold">Approved</span>
          </button>
        );
      return <span className="text-white/20 text-xs">—</span>;
    }

    if (!isOwnReport) {
      if (ticket?.status === "PENDING")
        return <span className="px-2 py-1 rounded-lg bg-yellow-500/20 text-yellow-300 border border-yellow-400/30 text-xs font-semibold">🎫 Pending</span>;
      if (ticket?.status === "APPROVED")
        return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-semibold">✅ Approved</span>;
      return <span className="text-white/20 text-xs">—</span>;
    }

    if (ticket?.status === "PENDING")
      return (
        <button
          onClick={() => handleCancelTicket(ticket.id)}
          disabled={cancellingId === ticket.id}
          className="px-2 py-1 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-400/30 text-xs font-semibold transition active:scale-95 flex items-center gap-1 mx-auto"
          title="Click to cancel this pending ticket"
        >
          🎫 Cancel
        </button>
      );
    if (ticket?.status === "APPROVED")
      return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-semibold">✅ Approved</span>;

    // Own report — can raise ticket (but not for auto-unpaid records)
    if (leave.isAutoUnpaid) {
      return (
        <span
          title="This leave was auto-created because no attendance was marked for the remaining half-day."
          className="px-2 py-1 rounded-lg bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs font-semibold"
        >
          🤖 Auto
        </span>
      );
    }

    return (
      <button
        onClick={() => {
          setTicketError("");
          setTicketReason("");
          setTicketModal({ leaveId: leave.id });
        }}
        className="px-2 py-1 rounded-lg bg-purple-600/80 hover:bg-purple-600 text-white
                   text-xs font-bold transition mx-auto flex items-center gap-1"
      >
        🎫 Raise Ticket
      </button>
    );
  };

  // ── layout helpers ────────────────────────────────────────────────────────

  const editIsSingleDay = SINGLE_DAY_TYPES.has(editForm.leave_type);

  const baseCols = 9 + (hasAnyCompOff ? 1 : 0) + (showSessionColumn && hasAnySession ? 1 : 0);
  const totalCols = baseCols + (showTicketReasonColumn ? 1 : 0) + (showEditColumn ? 1 : 0);

  const totalPages = Math.ceil(leaveSummary.length / pageSize) || 1;
  const currentLeaveSummary = useMemo(() => {
    return leaveSummary.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [leaveSummary, currentPage]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  const reportContent = (
    <>
      <div className={`flex flex-col items-center p-3 sm:p-6 w-full ${isPopup ? '' : 'min-h-screen'}`}>
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} className={`w-full ${isPopup ? '' : 'max-w-7xl'}`}>

          {/* HEADER — hidden in popup/raise-ticket mode */}
          {!isPopup && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500
                              flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-xl sm:text-2xl">📋</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-white">Leave Report</h2>
                <p className="text-white/60 text-xs sm:text-sm">View leave history and records</p>
              </div>
            </div>
              <button
                onClick={downloadCSV}
                disabled={leaveSummary.length === 0}
                className="self-start sm:self-auto px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700
                           disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold shadow transition"
              >
                ⬇ Download
              </button>
          </div>
          )}

          {/* FILTERS */}
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-5 sm:mb-6 items-end">
            {/* Month */}
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white
                         backdrop-blur-md appearance-none text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              <option value="0">All Months</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </option>
              ))}
            </select>

            {/* Year */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="bg-black/40 border border-white/20 rounded-lg px-3 sm:px-4 py-2 text-white
                         backdrop-blur-md appearance-none text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              <option value="0">All Years</option>
              {availableYears.length > 0
                ? availableYears.map((y) => <option key={y} value={String(y)}>{y}</option>)
                : <option value={DEFAULT_YEAR}>{DEFAULT_YEAR}</option>}
            </select>

            {isSuperAdmin && (
              <div className="relative">
                <button
                  onClick={() => setShowEditColumn((p) => !p)}
                  className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg font-semibold
                              text-xs sm:text-sm transition border ${showEditColumn
                      ? "bg-purple-600 border-purple-500 text-white"
                      : "bg-white/10 border-white/20 text-white/80 hover:bg-white/20"
                    }`}
                >
                  ✏️ {showEditColumn ? "Hide Edit" : "Edit Req."}
                  {pendingTicketCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-4 sm:h-5 px-1 bg-red-500 rounded-full
                                     text-white text-[10px] font-bold flex items-center justify-center
                                     border-2 border-slate-900 animate-pulse">
                      {pendingTicketCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* ── LEGEND for auto-unpaid ────────────────────────────────────── */}
          {leaveSummary.some((r) => r.leaves.some((l) => l.isAutoUnpaid)) && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500/10
                            border border-orange-500/20 text-orange-300 text-xs">
              <span className="text-sm">🤖</span>
              <span>
                <strong>AUTO</strong> leaves were created because no attendance was recorded for
                the remaining half of an approved half-day leave.
              </span>
            </div>
          )}

          {/* TABLE */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl w-full">
            <div className="overflow-x-auto no-scrollbar relative">
              {/* Swipe visual indicator for narrow screens */}
              <div className="md:hidden absolute right-2 top-2 z-10 animate-pulse text-[10px] bg-slate-950/80 text-white/50 px-2 py-0.5 rounded-full pointer-events-none select-none border border-white/5">
                ← Swipe table to view more →
              </div>
              <table className="w-full text-xs sm:text-sm border-collapse min-w-[600px]">
                <thead className="bg-gradient-to-r from-slate-700 to-slate-800">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Month - Year</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">CL</th>
                    {hasAnyCompOff && (
                      <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Comp Off</th>
                    )}
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Extra</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-left whitespace-nowrap">Date Range</th>
                    {showSessionColumn && hasAnySession && (
                      <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Session</th>
                    )}
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Leave Type</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Days</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-left whitespace-nowrap">Reason</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Status</th>
                    <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Action By</th>
                    {showTicketReasonColumn && (
                      <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-left whitespace-nowrap">🎫 Ticket</th>
                    )}
                    {showEditColumn && (
                      <th className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">Edit</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={totalCols} className="text-center py-8 text-white/60">
                        Loading leave history…
                      </td>
                    </tr>
                  )}
                  {!loading && leaveSummary.length === 0 && (
                    <tr>
                      <td colSpan={totalCols} className="text-center py-8 text-white/60">
                        No leave records found
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    currentLeaveSummary.map((record, idx) =>
                      record.leaves.map((leave, i) => {
                        const isEditing = editingId !== null && editingId === leave.id;
                        const ticket = ticketMap[leave.id];

                        return (
                          <tr
                            key={`${idx}-${i}`}
                            className={`border-b border-white/5 text-center transition ${isEditing
                              ? "bg-emerald-500/10 ring-1 ring-inset ring-emerald-400/40"
                              : leave.isAutoUnpaid
                                ? "bg-orange-500/5 hover:bg-orange-500/10"
                                : "hover:bg-white/5"
                              }`}
                          >
                            {/* Month-Year — rowSpan */}
                            {i === 0 && (
                              <td
                                rowSpan={record.leaves.length}
                                className="px-2 sm:px-4 py-3 sm:py-4 text-white font-medium
                                           border-r border-white/10 align-middle whitespace-nowrap text-xs sm:text-sm"
                              >
                                {record.month} {record.year}
                              </td>
                            )}

                            {/* CL Taken — rowSpan */}
                            {i === 0 && (
                              <td
                                rowSpan={record.leaves.length}
                                className="px-2 sm:px-4 py-3 sm:py-4 text-purple-300 font-bold
                                           text-center border-r border-white/10 align-middle"
                              >
                                {record.casualTaken}
                              </td>
                            )}

                            {/* Comp Off — rowSpan */}
                            {hasAnyCompOff && i === 0 && (
                              <td
                                rowSpan={record.leaves.length}
                                className="px-2 sm:px-4 py-3 sm:py-4 text-teal-300 font-bold
                                           text-center border-r border-white/10 align-middle"
                              >
                                {record.compOffTaken > 0 ? record.compOffTaken : <span className="text-white/20">—</span>}
                              </td>
                            )}

                            {/* Extra Taken — rowSpan */}
                            {i === 0 && (
                              <td
                                rowSpan={record.leaves.length}
                                className="px-2 sm:px-4 py-3 sm:py-4 text-rose-300 font-bold
                                           text-center border-r border-white/10 align-middle"
                              >
                                {record.extraTaken}
                              </td>
                            )}

                            {/* DATE */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4 text-white/80 whitespace-nowrap">
                              {isEditing ? (
                                <div className="flex flex-col gap-1 text-left min-w-[120px] sm:min-w-[130px]">
                                  <label className="text-white/40 text-[10px]">From</label>
                                  <input
                                    type="date"
                                    value={editForm.leave_date_from || ""}
                                    onChange={(e) => handleFromChange(e.target.value)}
                                    className="bg-white/5 border border-emerald-400/50 rounded px-2 py-1
                                               text-white text-xs w-full focus:outline-none focus:border-emerald-400"
                                  />
                                  <label className="text-white/40 text-[10px] mt-1">To</label>
                                  <input
                                    type="date"
                                    value={editForm.leave_date_to || ""}
                                    min={editForm.leave_date_from || ""}
                                    disabled={editIsSingleDay}
                                    onChange={(e) => setEditForm((f) => ({ ...f, leave_date_to: e.target.value }))}
                                    className={`bg-white/5 border border-emerald-400/50 rounded px-2 py-1
                                                text-white text-xs w-full focus:outline-none focus:border-emerald-400
                                                ${editIsSingleDay ? "opacity-40 cursor-not-allowed" : ""}`}
                                  />
                                  {editIsSingleDay && (
                                    <p className="text-white/30 text-[10px]">Auto-set (single-day)</p>
                                  )}
                                </div>
                              ) : (
                                leave.date
                              )}
                            </td>

                            {/* TYPE */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4">
                              {isEditing ? (
                                <select
                                  value={editForm.leave_type || ""}
                                  onChange={(e) => handleTypeChange(e.target.value)}
                                  className="bg-slate-800 border border-emerald-400/50 rounded px-1 sm:px-2 py-1
                                             text-white text-xs w-full focus:outline-none focus:border-emerald-400"
                                >
                                  {leaveTypes.map((lt) => (
                                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`px-2 py-0.5 sm:py-1 rounded-lg border text-xs whitespace-nowrap ${
                                    leave.rawType === "MEDICAL" || leave.type?.toLowerCase().includes("medical")
                                      ? "bg-purple-500/20 text-purple-200 border-purple-400/30"
                                      : "bg-blue-500/20 text-blue-200 border-blue-400/20"
                                  }`}>
                                    {leave.type}
                                    {leave.isAutoUnpaid && <AutoUnpaidBadge />}
                                  </span>
                                    {leave.medical_document && (
                                      <div className="flex items-center gap-1.5 mt-0.5">
                                        <button
                                          type="button"
                                          onClick={() => handleDirectPreview(leave.medical_document)}
                                          title="Preview Medical Certificate"
                                          className="px-1.5 py-0.5 rounded bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 text-[10px] font-semibold border border-purple-400/40 flex items-center gap-0.5 transition-colors"
                                        >
                                          Preview
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDirectDownload(leave.medical_document, leave.medical_document_name)}
                                          title="Download Medical Certificate"
                                          className="px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-[10px] border border-white/20 flex items-center gap-0.5 transition-colors"
                                        >
                                          Download
                                        </button>
                                      </div>
                                    )}
                                </div>
                              )}
                            </td>

                            {/* DAYS */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4 text-white text-center whitespace-nowrap">
                              {leave.days}
                            </td>

                            {/* SESSION */}
                            {showSessionColumn && hasAnySession && (
                              <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                                {isEditing ? (
                                  editIsSingleDay ? (
                                    <select
                                      value={editForm.half_day_session || ""}
                                      className="bg-slate-800 border border-purple-500/30 rounded px-1.5 py-1 text-white text-xs focus:outline-none"
                                      onChange={(e) => setEditForm((f) => ({ ...f, half_day_session: e.target.value || null }))}
                                    >
                                      <option value="">Full Day</option>
                                      <option value="HALF_DAY_FIRST">Morning Session</option>
                                      <option value="HALF_DAY_SECOND">Afternoon Session</option>
                                    </select>
                                  ) : (
                                    <span className="text-white/20 italic">—</span>
                                  )
                                ) : leave.halfDaySession ? (
                                  <SessionBadge session={leave.halfDaySession} />
                                ) : (
                                  <span className="text-white/20 text-xs">—</span>
                                )}
                              </td>
                            )}

                            {/* REASON */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4 text-white/70 italic max-w-[100px] sm:max-w-[160px] truncate">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editForm.reason || ""}
                                  onChange={(e) => setEditForm((f) => ({ ...f, reason: e.target.value }))}
                                  className="bg-white/5 border border-emerald-400/50 rounded px-2 py-1
                                             text-white text-xs w-full focus:outline-none focus:border-emerald-400"
                                  placeholder="Reason"
                                />
                              ) : leave.isAutoUnpaid ? (
                                <span className="text-orange-300/70 text-[10px] italic">
                                  Auto: no attendance for remaining half
                                </span>
                              ) : (
                                leave.reason || "—"
                              )}
                            </td>

                            {/* STATUS */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4">
                              {isEditing ? (
                                <select
                                  value={editForm.status || ""}
                                  className="bg-slate-800 border border-purple-500/30 rounded px-1.5 py-1 text-white text-xs focus:outline-none"
                                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                                >
                                  {LEAVE_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              ) : (
                                <StatusBadge status={leave.status} />
                              )}
                            </td>

                            {/* ACTION BY */}
                            <td className="px-2 sm:px-4 py-3 sm:py-4 text-white/80 whitespace-nowrap">
                              {leave.action_by || "—"}
                            </td>

                            {/* TICKET REASON */}
                            {showTicketReasonColumn && (
                              <td className="px-2 sm:px-4 py-3 sm:py-4 text-left max-w-[200px]" title={ticket?.reason || ""}>
                                <div className="text-white/70 text-[11px] leading-relaxed break-words line-clamp-2">
                                  {ticket?.reason || "—"}
                                </div>
                              </td>
                            )}
                            {showEditColumn && (
                              <td className="py-2 px-3 whitespace-nowrap">
                                {isEditing ? (
                                  <div className="flex flex-col items-center gap-1">
                                    {editError && (
                                      <p className="text-red-400 text-[10px] mb-1 max-w-[140px] sm:max-w-[180px]
                                                    text-center leading-tight">
                                        ⚠️ {editError}
                                      </p>
                                    )}
                                    <div className="flex gap-1 sm:gap-2">
                                      <button
                                        onClick={() => handleSaveEdit(leave.id)}
                                        disabled={editSaving}
                                        className="px-2 sm:px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500
                                                   disabled:opacity-50 text-white text-xs font-bold transition"
                                      >
                                        {editSaving ? "…" : "✅"}
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="px-2 sm:px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20
                                                   text-white text-xs font-semibold transition"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <TicketCell leave={leave} />
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {!loading && leaveSummary.length > pageSize && (
              <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02] border-t border-white/5 flex-wrap gap-2">
                <div className="text-white/60 text-xs select-none">
                  Showing <span className="font-semibold text-white">{Math.min((currentPage - 1) * pageSize + 1, leaveSummary.length)}</span> to <span className="font-semibold text-white">{Math.min(currentPage * pageSize, leaveSummary.length)}</span> of <span className="font-semibold text-white">{leaveSummary.length}</span> month-groups
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold disabled:opacity-40 disabled:hover:bg-white/5 transition"
                  >
                    ← Previous
                  </button>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className="px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold disabled:opacity-40 disabled:hover:bg-white/5 transition"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isPopup && (
            <div className="flex justify-start mt-4">
              <button
                onClick={handleBack}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white
                           text-sm font-semibold transition border border-white/20 shadow-md"
              >
                ← Back
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {/* RAISE TICKET MODAL */}
      <AnimatePresence>
        {ticketModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center
                       bg-black/60 backdrop-blur-sm p-3 sm:p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md bg-slate-900 rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-6"
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">🎫 Raise Edit Ticket</h3>
              <p className="text-white/50 text-xs sm:text-sm mb-4">
                Describe what needs to be corrected. The admin will review and apply the fix.
              </p>
              <textarea
                rows={4}
                value={ticketReason}
                onChange={(e) => setTicketReason(e.target.value)}
                placeholder="e.g. Applied as Half Day but it should be a Full Day Sick Leave."
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white
                           text-sm placeholder-white/30 resize-none focus:outline-none focus:border-purple-400"
              />
              {ticketError && <p className="text-red-400 text-xs mt-2">{ticketError}</p>}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setTicketModal(null); setTicketReason(""); setTicketError(""); }}
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRaiseTicket}
                  disabled={ticketSubmitting}
                  className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50
                             text-white text-sm font-bold transition"
                >
                  {ticketSubmitting ? "Submitting…" : "Submit Ticket"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (isPopup) {
    return (
      <div className="w-full text-white text-xs sm:text-sm overflow-y-auto max-h-[85vh] no-scrollbar relative" style={{ scrollbarWidth: 'none' }}>
        {reportContent}
      </div>
    );
  }

  return (
    <Layout>
      {reportContent}
    </Layout>
  );
}

export default LeaveReport;
